import type { CardFormat } from './types'
import { EXPECTED_DIMS, REQUIRED_PLACEHOLDERS, REQUIRED_IMAGE_PLACEHOLDERS } from './templateTypes'

export interface TemplateValidationResult {
  valid: boolean
  /** Human-readable error lines; empty when valid. */
  errors: string[]
}

/**
 * Validate an uploaded template HTML string against the rules for a format:
 *   1. All required data-placeholder ids are present.
 *   2. Image placeholders carry data-placeholder-type="image".
 *   3. The `.main` root renders at the expected dimensions.
 *
 * Dimension checking renders the HTML in an off-screen iframe, so this is async.
 */
export async function validateTemplate(
  html: string,
  format: CardFormat
): Promise<TemplateValidationResult> {
  const errors: string[] = []

  // ── Parse markup (no rendering needed for placeholder checks) ──
  const doc = new DOMParser().parseFromString(html, 'text/html')

  const main = doc.querySelector('.main')
  if (!main) {
    errors.push('Missing a root element with class "main".')
  }

  REQUIRED_PLACEHOLDERS.forEach((key) => {
    const el = doc.querySelector(`[data-placeholder="${key}"]`)
    if (!el) {
      errors.push(`Missing required placeholder: data-placeholder="${key}".`)
      return
    }
    const isImage = (REQUIRED_IMAGE_PLACEHOLDERS as readonly string[]).includes(key)
    if (isImage && el.getAttribute('data-placeholder-type') !== 'image') {
      errors.push(`Placeholder "${key}" must be an <img> with data-placeholder-type="image".`)
    }
  })

  // ── Dimension check (only if markup looks sane) ──
  const { w, h } = EXPECTED_DIMS[format]
  if (main) {
    const ok = await checkRenderedDimensions(html, w, h)
    if (!ok) {
      errors.push(
        `Wrong dimensions — the ".main" element must render at ${w}×${h}px for ${format}.`
      )
    }
  }

  return { valid: errors.length === 0, errors }
}

/** Renders HTML off-screen and verifies the `.main` box matches expected px. */
function checkRenderedDimensions(
  html: string,
  expectedW: number,
  expectedH: number
): Promise<boolean> {
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe')
    iframe.style.cssText =
      'position:fixed;left:-9999px;top:0;width:1200px;height:2200px;border:none;'
    document.body.appendChild(iframe)

    const cleanup = (result: boolean) => {
      if (iframe.parentNode) document.body.removeChild(iframe)
      resolve(result)
    }

    const doc = iframe.contentDocument
    if (!doc) {
      cleanup(false)
      return
    }
    doc.open()
    doc.write(html)
    doc.close()

    setTimeout(() => {
      const mainEl = doc.querySelector('.main') as HTMLElement | null
      if (!mainEl) {
        cleanup(false)
        return
      }
      const rect = mainEl.getBoundingClientRect()
      const valid = Math.abs(rect.width - expectedW) < 5 && Math.abs(rect.height - expectedH) < 5
      cleanup(valid)
    }, 600)
  })
}
